import { useEffect, useMemo, useState } from 'react';
import { callListFeedback, callUpdateFeedbackStatus, callDeleteFeedback, type AdminFeedback, type FeedbackStatus } from '../firebase';
import { filterFeedback, REASON_LABELS, STATUS_LABELS } from './feedbackFilter';

const STATUS_OPTIONS: FeedbackStatus[] = ['new', 'seen', 'resolved'];

export function Feedback() {
  const [rows, setRows] = useState<AdminFeedback[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState('all');
  const [reason, setReason] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<AdminFeedback | null>(null);

  async function loadPage(fromCursor: number | null, append: boolean) {
    setLoading(true);
    setErr(null);
    try {
      const res = await callListFeedback({ cursor: fromCursor ?? undefined, pageSize: 200 });
      setRows((prev) => (append ? [...prev, ...res.data.items] : res.data.items));
      setCursor(res.data.nextCursor);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPage(null, false);
  }, []);

  const filtered = useMemo(() => filterFeedback(rows, { status, reason, query }), [rows, status, reason, query]);
  const newCount = useMemo(() => rows.filter((r) => r.status === 'new').length, [rows]);

  async function changeStatus(item: AdminFeedback, next: FeedbackStatus) {
    setErr(null);
    try {
      await callUpdateFeedbackStatus({ id: item.id, status: next });
      setRows((prev) => prev.map((r) => (r.id === item.id ? { ...r, status: next } : r)));
      setSelected((prev) => (prev && prev.id === item.id ? { ...prev, status: next } : prev));
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function deleteRow(item: AdminFeedback) {
    if (!window.confirm('¿Borrar este feedback? No se puede deshacer.')) {
      return;
    }
    setErr(null);
    try {
      await callDeleteFeedback({ id: item.id });
      setRows((prev) => prev.filter((r) => r.id !== item.id));
      setSelected((prev) => {
        if (prev && prev.id === item.id) {
          return null;
        }
        return prev;
      });
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div>
      <div className="row between" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>
          Feedback ({rows.length}) · {newCount} nuevos
        </h2>
        <button onClick={() => loadPage(null, false)} disabled={loading}>
          {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </div>

      <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <select value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="all">Todos los motivos</option>
          {Object.entries(REASON_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Buscar en mensaje, email o UID…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
      </div>

      {err && <div className="error">{err}</div>}

      <div className="panel" style={{ padding: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Motivo</th>
              <th>Usuario</th>
              <th>Mensaje</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} onClick={() => setSelected(r)} style={{ cursor: 'pointer' }}>
                <td className="muted">{formatDate(r.createdAt)}</td>
                <td>
                  <span className="badge">{STATUS_LABELS[r.status] ?? r.status}</span>
                </td>
                <td>{REASON_LABELS[r.reason] ?? r.reason}</td>
                <td>{r.email || r.uid}</td>
                <td>{truncate(r.message)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="danger"
                    title="Borrar feedback"
                    onClick={(e) => {
                      e.stopPropagation();
                      void deleteRow(r);
                    }}
                  >
                    🗑
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                  Sin feedback.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {cursor !== null && (
        <div className="pagination">
          <button onClick={() => loadPage(cursor, true)} disabled={loading}>
            Cargar más
          </button>
        </div>
      )}

      {selected && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="row between">
            <h3 style={{ margin: 0 }}>{REASON_LABELS[selected.reason] ?? selected.reason}</h3>
            <div className="row" style={{ gap: 8 }}>
              <select value={statusOf(selected)} onChange={(e) => changeStatus(selected, e.target.value as FeedbackStatus)}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
              <button className="danger" onClick={() => void deleteRow(selected)}>
                Borrar
              </button>
            </div>
          </div>
          <p style={{ whiteSpace: 'pre-wrap' }}>{selected.message}</p>
          <div className="muted" style={{ fontSize: 13 }}>
            <div>Usuario: {selected.email || selected.uid}</div>
            <div>Fecha: {formatDate(selected.createdAt)}</div>
            <div>Versión: {selected.context?.appVersion ?? '—'}</div>
            <div>Plataforma: {selected.context?.platform ?? '—'}</div>
            <div>Ruta: {selected.context?.route ?? '—'}</div>
            <div>UserAgent: {selected.context?.userAgent ?? '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function statusOf(f: AdminFeedback): FeedbackStatus {
  if (f.status === 'seen') {
    return 'seen';
  }
  if (f.status === 'resolved') {
    return 'resolved';
  }
  return 'new';
}

function truncate(s: string): string {
  if (s.length <= 80) {
    return s;
  }
  return `${s.slice(0, 80)}…`;
}

function formatDate(ms: number | null): string {
  if (!ms) {
    return '—';
  }
  return new Date(ms).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}
